import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { CountryProps } from '../../pages/Cannabis';

interface Question {
  id: string;
  text: string;
  options: { value: string; label: string; desc: string }[];
}

const QUESTIONS: Question[] = [
  { id: 'health', text: 'How important is health/lung safety to you?', options: [
    { value: 'critical', label: 'Very important', desc: 'I want the healthiest option available' },
    { value: 'moderate', label: 'Somewhat important', desc: 'I\'ll trade some health for convenience' },
    { value: 'low', label: 'Not a priority', desc: 'I care more about experience than health' },
  ]},
  { id: 'discretion', text: 'How discreet do you need to be?', options: [
    { value: 'stealth', label: 'Maximum stealth', desc: 'No smell, no visibility, no evidence' },
    { value: 'some', label: 'Somewhat discreet', desc: 'Minimal smell is fine, not in public' },
    { value: 'none', label: 'Don\'t care', desc: 'I\'m in a legal, private space' },
  ]},
  { id: 'onset', text: 'How quickly do you want to feel effects?', options: [
    { value: 'instant', label: 'Immediately', desc: 'I want to feel it within minutes' },
    { value: 'moderate', label: '15-30 minutes is fine', desc: 'I can wait a bit' },
    { value: 'slow', label: 'I\'m patient', desc: 'Long-lasting is more important than fast onset' },
  ]},
  { id: 'duration', text: 'How long do you want effects to last?', options: [
    { value: 'short', label: '1-2 hours', desc: 'Quick session, back to normal soon' },
    { value: 'medium', label: '2-4 hours', desc: 'A good evening session' },
    { value: 'long', label: '4-8 hours', desc: 'All day or all night relief' },
  ]},
  { id: 'budget', text: 'What\'s your budget situation?', options: [
    { value: 'low', label: 'Budget-friendly', desc: 'I want the most bang for my buck' },
    { value: 'mid', label: 'Mid-range', desc: 'Happy to invest in quality' },
    { value: 'high', label: 'Money is no object', desc: 'I want the premium experience' },
  ]},
];

interface MethodResult {
  name: string;
  match: number;
  desc: string;
  pros: string[];
  cons: string[];
  color: string;
}

function scoreMethod(answers: Record<string, string>): MethodResult[] {
  const methods: MethodResult[] = [
    { name: 'Dry Herb Vaporizer', match: 0, desc: 'Heat flower without combustion. Best balance of health, flavor, and efficiency.', pros: ['Healthiest inhalation method', 'Great flavor', 'Efficient', 'AVB can be eaten'], cons: ['$100-400 upfront cost', 'Learning curve', 'Battery life'], color: '#4ade80' },
    { name: 'Joint', match: 0, desc: 'Classic rolled cannabis in paper. Simple, social, ritualistic.', pros: ['No equipment needed', 'Easy to share', 'Iconic experience', 'Cheap'], cons: ['Combustion byproducts', 'Less efficient', 'Smell'], color: '#d4a574' },
    { name: 'Edibles', match: 0, desc: 'Cannabis-infused food. Longest-lasting, most potent per dose.', pros: ['No lung exposure', 'Long-lasting (4-8hrs)', 'Precise dosing (commercial)', 'Discreet'], cons: ['Slow onset (1-2hrs)', 'Easy to overdo', 'Hard to dose homemade'], color: '#a78bfa' },
    { name: 'Vape Pen (Cartridge)', match: 0, desc: 'Pre-filled THC oil cartridge on a battery. Ultimate discretion.', pros: ['Extremely discreet', 'No smell', 'Portable', 'Easy to use'], cons: ['Only buy from licensed sources', 'Less flavorful', 'Environmental waste'], color: '#60a5fa' },
    { name: 'Tincture (Sublingual)', match: 0, desc: 'Drops under the tongue. Fast-ish onset, precise dosing, no lung involvement.', pros: ['No smoking/vaping', 'Precise dosing', 'Faster than edibles (15-30min)', 'Discreet'], cons: ['Alcohol taste', 'Effects shorter than edibles', 'Boring (no ritual)'], color: '#f59e0b' },
    { name: 'Bong / Water Pipe', match: 0, desc: 'Water-filtered hits. Smooth, efficient, but not discreet at all.', pros: ['Smooth water-filtered hits', 'Very efficient', 'Cool smoke', 'Reusable'], cons: ['Not portable', 'Still combustion', 'Requires cleaning', 'Conspicuous'], color: '#ec4899' },
    { name: 'Dab Rig', match: 0, desc: 'Vaporize cannabis concentrates. Extreme potency. Expert level.', pros: ['Extremely potent', 'Great flavor (low temp)', 'Efficient', 'Fast acting'], cons: ['Expensive setup', 'Steep learning curve', 'Very high tolerance build-up'], color: '#ef4444' },
    { name: 'Topical', match: 0, desc: 'Cannabis cream/balm applied to skin. Pain relief without any high.', pros: ['No psychoactive effects', 'Targeted relief', 'No tolerance', 'Safe with medications'], cons: ['Won\'t get you high', 'Localized only', 'Can be pricey'], color: '#06b6d4' },
  ];

  // Score each method
  const { health, discretion, onset, duration, budget } = answers;

  methods.forEach(m => {
    // Health scoring
    if (health === 'critical') {
      if (['Edibles', 'Tincture (Sublingual)', 'Topical'].includes(m.name)) m.match += 3;
      else if (m.name === 'Dry Herb Vaporizer') m.match += 2;
      else if (m.name === 'Vape Pen (Cartridge)') m.match += 1;
    } else if (health === 'moderate') {
      if (['Dry Herb Vaporizer', 'Vape Pen (Cartridge)'].includes(m.name)) m.match += 2;
      else m.match += 1;
    } else { m.match += 1; }

    // Discretion
    if (discretion === 'stealth') {
      if (['Edibles', 'Tincture (Sublingual)', 'Vape Pen (Cartridge)', 'Topical'].includes(m.name)) m.match += 3;
    } else if (discretion === 'some') {
      if (['Dry Herb Vaporizer', 'Vape Pen (Cartridge)', 'Edibles', 'Tincture (Sublingual)'].includes(m.name)) m.match += 2;
      else m.match += 1;
    } else { m.match += 1; }

    // Onset
    if (onset === 'instant') {
      if (['Joint', 'Bong / Water Pipe', 'Dab Rig', 'Dry Herb Vaporizer', 'Vape Pen (Cartridge)'].includes(m.name)) m.match += 3;
    } else if (onset === 'moderate') {
      if (m.name === 'Tincture (Sublingual)') m.match += 3;
      else m.match += 1;
    } else {
      if (m.name === 'Edibles') m.match += 3;
      else m.match += 1;
    }

    // Duration
    if (duration === 'long') {
      if (m.name === 'Edibles') m.match += 3;
      else if (m.name === 'Topical') m.match += 2;
      else m.match += 1;
    } else if (duration === 'short') {
      if (['Joint', 'Dry Herb Vaporizer', 'Vape Pen (Cartridge)', 'Bong / Water Pipe'].includes(m.name)) m.match += 2;
    } else { m.match += 1; }

    // Budget
    if (budget === 'low') {
      if (['Joint', 'Bong / Water Pipe', 'Edibles'].includes(m.name)) m.match += 2;
    } else if (budget === 'high') {
      if (['Dab Rig', 'Dry Herb Vaporizer'].includes(m.name)) m.match += 2;
      else m.match += 1;
    } else { m.match += 1; }
  });

  return methods.sort((a, b) => b.match - a.match);
}

export default function MethodFinderTab(_props: CountryProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [results, setResults] = useState<MethodResult[] | null>(null);

  function answer(questionId: string, value: string) {
    const updated = { ...answers, [questionId]: value };
    setAnswers(updated);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setResults(scoreMethod(updated));
    }
  }

  function reset() {
    setStep(0);
    setAnswers({});
    setResults(null);
  }

  const question = QUESTIONS[step];

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-white">Find Your Method</h2>
        <p className="text-white/40 text-sm">Answer 5 questions and we'll recommend the best consumption method for you.</p>
      </div>

      {!results ? (
        <>
          {/* Progress */}
          <div className="flex gap-1">
            {QUESTIONS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < step ? 'bg-[#4ade80]' : i === step ? 'bg-[#4ade80]/50' : 'bg-white/10'}`} />
            ))}
          </div>

          {/* Question */}
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="space-y-4">
              <p className="text-white/40 text-xs">Question {step + 1} of {QUESTIONS.length}</p>
              <h3 className="text-white font-bold text-lg">{question.text}</h3>
              <div className="space-y-2">
                {question.options.map(opt => (
                  <button key={opt.value} onClick={() => answer(question.id, opt.value)}
                    className="w-full text-left p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#4ade80]/30 hover:bg-[#4ade80]/5 transition-colors">
                    <p className="text-white font-semibold text-sm">{opt.label}</p>
                    <p className="text-white/40 text-xs mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)} className="text-white/30 text-xs hover:text-white/50">Back</button>
              )}
            </motion.div>
          </AnimatePresence>
        </>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <h3 className="text-white font-bold text-lg">Your Results</h3>
          {results.map((method, i) => (
            <div key={method.name} className={`rounded-2xl border p-4 space-y-2 ${i === 0 ? 'border-opacity-40' : 'border-white/5 bg-white/[0.01]'}`}
              style={i === 0 ? { borderColor: method.color + '66', backgroundColor: method.color + '10' } : {}}>
              <div className="flex items-center gap-3">
                {i === 0 && <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-[#4ade80]/20 text-[#4ade80]">BEST MATCH</span>}
                <h4 className="font-bold text-sm" style={{ color: i === 0 ? method.color : 'white' }}>{method.name}</h4>
                <span className="text-white/20 text-xs ml-auto">{Math.round((method.match / 15) * 100)}% match</span>
              </div>
              <p className="text-white/50 text-xs">{method.desc}</p>
              {i < 3 && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <p className="text-[#4ade80]/60 text-[10px] font-semibold">Pros</p>
                    {method.pros.map(p => <p key={p} className="text-white/40 text-[11px]">+ {p}</p>)}
                  </div>
                  <div>
                    <p className="text-red-400/60 text-[10px] font-semibold">Cons</p>
                    {method.cons.map(c => <p key={c} className="text-white/40 text-[11px]">- {c}</p>)}
                  </div>
                </div>
              )}
            </div>
          ))}
          <button onClick={reset} className="w-full py-3 rounded-xl border border-white/10 text-white/50 text-sm hover:bg-white/5 transition-colors">
            Take Quiz Again
          </button>
        </motion.div>
      )}
    </div>
  );
}
